import { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import PythonCodeEditor from './PythonCodeEditor';
import InputDialog from './InputDialog';
import PyodideLoadingIndicator from './PyodideLoadingIndicator';
import { supabase } from '../supabase/client';
import {
  runPythonSmart,
  getPyodideStatus,
} from '../services/pythonExecution';
import { usePyodidePreload } from '../hooks/usePyodidePreload';

interface PythonCodingAnswerAreaProps {
  question: {
    id: string;
    title: string;
    content: string;
    starterCode?: string;
    referenceSolution?: string;
    timeLimitMs?: number;
    memoryLimitMb?: number;
    score?: number;
  };
  paperId: string;
  isSubmitted: boolean;
  answer?: string;
  onAnswerChange: (answer: string) => void;
}

export default function PythonCodingAnswerArea({
  question,
  paperId,
  isSubmitted,
  answer,
  onAnswerChange,
}: PythonCodingAnswerAreaProps) {
  // Pyodide 预加载状态
  const pyodidePreload = usePyodidePreload();

  // 初始化埋点对象（如果不存在）
  useEffect(() => {
    if (!(window as any).__PYODIDE_METRICS__) {
      (window as any).__PYODIDE_METRICS__ = {
        loadSuccessCount: 0,
        loadFailCount: 0,
        totalLoadTime: 0,
        executionCount: 0,
        localExecutionCount: 0,
        cloudExecutionCount: 0,
        fallbackToCloudCount: 0,
        totalExecutionTime: 0,
      };
    }
  }, []);

  const [code, setCode] = useState(answer || question.starterCode || '');
  const [isRunning, setIsRunning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [executionTime, setExecutionTime] = useState(0);
  const [turtleSvg, setTurtleSvg] = useState<string | null>(null);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [executionEngine, setExecutionEngine] = useState<'local' | 'cloud' | null>(null);

  // Input 对话框状态
  const [showInputDialog, setShowInputDialog] = useState(false);
  const [pendingCode, setPendingCode] = useState<string>('');
  const [inputCount, setInputCount] = useState(0);

  // 检查是否已提交
  useEffect(() => {
    if (answer && answer.length > 0) {
      setSubmissionId(answer);
    }
  }, [answer]);

  // 检测代码中的 input() 调用次数
  const detectInputCount = useCallback((code: string): number => {
    const matches = code.match(/\binput\s*\(/g);
    return matches ? matches.length : 0;
  }, []);

  // 优化后的运行代码 - 使用智能双引擎执行
  const handleRun = useCallback(async (currentCode: string) => {
    // 检测 input() 调用
    const count = detectInputCount(currentCode);

    if (count > 0) {
      // 弹出输入对话框
      setPendingCode(currentCode);
      setInputCount(count);
      setShowInputDialog(true);
      return;
    }

    // 无 input() 调用，直接执行
    await executeCode(currentCode, '');
  }, [detectInputCount]);

  // 处理输入对话框确认
  const handleInputDialogConfirm = useCallback(async (inputs: string[]) => {
    setShowInputDialog(false);
    const combinedInput = inputs.join('\n');
    await executeCode(pendingCode, combinedInput);
  }, [pendingCode]);

  // 处理输入对话框取消
  const handleInputDialogCancel = useCallback(() => {
    setShowInputDialog(false);
    setPendingCode('');
    setInputCount(0);
  }, []);

  // 实际执行代码的函数
  const executeCode = useCallback(async (currentCode: string, input: string) => {
    setIsRunning(true);
    setOutput('');
    setError('');
    setTurtleSvg(null);
    setExecutionEngine(null);

    const startTime = Date.now();

    try {
      // 添加60秒超时控制（云端执行可能需要更长时间）
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('执行超时（60秒）')), 60000);
      });

      const result = await Promise.race([
        runPythonSmart(currentCode, input),
        timeoutPromise
      ]);

      const elapsed = Date.now() - startTime;
      setExecutionTime(elapsed);
      setOutput(result.output);
      setError(result.error || '');
      setExecutionEngine(result.engine);

      if (result.svg) {
        setTurtleSvg(result.svg);
      }

      // 埋点：记录执行成功
      const metrics = (window as any).__PYODIDE_METRICS__;
      if (metrics) {
        metrics.executionCount++;
        metrics.totalExecutionTime += elapsed;
        if (result.engine === 'local') {
          metrics.localExecutionCount++;
        } else {
          metrics.cloudExecutionCount++;
        }
      }
    } catch (err: any) {
      setExecutionTime(Date.now() - startTime);
      setError(err.message || '执行失败');

      // 埋点：记录执行失败
      const metrics = (window as any).__PYODIDE_METRICS__;
      if (metrics) {
        metrics.executionCount++;
      }
    } finally {
      setIsRunning(false);
    }

    setCode(currentCode);
  }, []);

  // 保存代码为.py文件并创建提交记录
  const handleSubmit = useCallback(async (currentCode: string) => {
    if (!currentCode.trim()) {
      setError('代码不能为空');
      return;
    }

    // 防重复提交：如果正在提交或已提交，直接返回
    if (isSubmitting || submissionId) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // 获取当前用户
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('请先登录');
        setIsSubmitting(false);
        return;
      }

      // 1. 将代码保存为.py文件上传到存储桶
      const fileName = `python_submissions/${user.id}/${question.id}_${Date.now()}.py`;
      const fileBlob = new Blob([currentCode], { type: 'text/x-python' });
      const arrayBuffer = await fileBlob.arrayBuffer();

      const { error: uploadError } = await supabase.storage
        .from('exam-answers')
        .upload(fileName, arrayBuffer, {
          contentType: 'text/x-python',
          upsert: true,
        });

      if (uploadError) {
        throw new Error(`上传代码文件失败: ${uploadError.message}`);
      }

      // 获取文件URL
      const { data: { publicUrl } } = supabase.storage
        .from('exam-answers')
        .getPublicUrl(fileName);

      // 2. 创建提交记录，状态为 pending（待阅卷）
      // paperId 可能是 'custom'，需要处理为 null
      const validPaperId = paperId && paperId !== 'custom' ? paperId : null;

      const { data: submissionData, error: submissionError } = await supabase
        .from('python_submissions')
        .insert({
          user_id: user.id,
          question_id: question.id,
          paper_id: validPaperId,
          code: currentCode,
          code_file_url: publicUrl,
          status: 'pending',
          score: 0,
          execution_time_ms: 0,
          memory_used_mb: 0,
          test_results: '[]',
        })
        .select()
        .single();

      if (submissionError) {
        // 处理 409 冲突错误
        if (submissionError.code === '409' || submissionError.message?.includes('conflict')) {
          throw new Error('您已经提交过答案，请勿重复提交');
        }
        throw new Error(`保存提交记录失败: ${submissionError.message}`);
      }

      // 3. 更新本地状态和父组件
      setCode(currentCode);
      setSubmissionId(submissionData.id);
      onAnswerChange(submissionData.id);

    } catch (err: any) {
      setError(err.message || '提交失败');
    } finally {
      setIsSubmitting(false);
    }
  }, [question.id, paperId, onAnswerChange, isSubmitting, submissionId]);

  return (
    <div className="space-y-4">
      {/* Pyodide 加载进度指示器 */}
      <PyodideLoadingIndicator
        status={pyodidePreload.status}
        progress={pyodidePreload.progress}
        error={pyodidePreload.error}
      />

      <PythonCodeEditor
        initialCode={code}
        starterCode={question.starterCode || ''}
        onRun={handleRun}
        onSubmit={submissionId ? undefined : handleSubmit}
        isRunning={isRunning}
        isSubmitting={isSubmitting}
        output={output}
        error={error}
        executionTime={executionTime}
        turtleSvg={turtleSvg}
        readOnly={!!submissionId}
        height="350px"
        submitLabel="保存并提交"
      />

      {/* 执行引擎标识 */}
      {executionEngine && !isRunning && output && (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded">
            {executionEngine === 'local' ? '本地执行' : '云端执行'}
          </span>
          <span>耗时 {executionTime}ms</span>
        </div>
      )}

      {/* 提交成功提示 */}
      {submissionId && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 rounded-lg border border-green-200 p-4"
        >
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <span className="font-bold text-green-700">答案已保存</span>
              <span className="text-sm text-gray-600 ml-2">
                等待教师阅卷
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            您的代码已保存为 .py 文件，教师将在后台进行人工阅卷或自动判卷。
          </p>
        </motion.div>
      )}

      {/* Input 输入对话框 */}
      <InputDialog
        isOpen={showInputDialog}
        inputCount={inputCount}
        onConfirm={handleInputDialogConfirm}
        onCancel={handleInputDialogCancel}
      />
    </div>
  );
}
